/** Hook to load all map data for a municipality (species, measures, layers).

Fetches municipality + species + measures + all base layers in parallel.
Re-fetches when gemeinde changes. Does NOT re-fetch when filters change
(filtering is client-side on cached GeoJSON).
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { apiClient } from "../api/client";
import type { GeoJSONFeatureCollection, RegionConfig } from "../types";

interface UseMapDataResult {
  municipalityData: GeoJSONFeatureCollection | null;
  speciesData: GeoJSONFeatureCollection | null;
  measuresData: GeoJSONFeatureCollection | null;
  layersData: Record<string, GeoJSONFeatureCollection>;
  loading: boolean;
  error: string | null;
}

export function useMapData(gemeinde: string | null, config: RegionConfig | null): UseMapDataResult {
  const [municipalityData, setMunicipalityData] = useState<GeoJSONFeatureCollection | null>(null);
  const [speciesData, setSpeciesData] = useState<GeoJSONFeatureCollection | null>(null);
  const [measuresData, setMeasuresData] = useState<GeoJSONFeatureCollection | null>(null);
  const [layersData, setLayersData] = useState<Record<string, GeoJSONFeatureCollection>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const layerNames = useMemo(() => {
    if (!config) return [];
    return Object.keys(config.base_layers);
  }, [config]);

  const fetchData = useCallback(async () => {
    if (!gemeinde) {
      setMunicipalityData(null);
      setSpeciesData(null);
      setMeasuresData(null);
      setLayersData({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch the focus boundary, species, measures, and base layers in parallel.
      const layerPromises = layerNames.map((name) =>
        apiClient.getLayer(name, gemeinde).then((data) => [name, data] as const),
      );

      const [municipality, species, measures, ...layerResults] = await Promise.all([
        apiClient.getGemeinde(gemeinde),
        apiClient.getSpecies(gemeinde),
        apiClient.getMeasures(gemeinde),
        ...layerPromises,
      ]);

      setMunicipalityData(municipality);
      setSpeciesData(species);
      setMeasuresData(measures);

      const layers: Record<string, GeoJSONFeatureCollection> = {};
      for (const [name, data] of layerResults) {
        layers[name] = data;
      }
      setLayersData(layers);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [gemeinde, layerNames]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { municipalityData, speciesData, measuresData, layersData, loading, error };
}
